<template>
  <div class="section-normal bg-color-tertiary featured-section" v-if="cars.length > 0 || isLoading">
    <div class="container">
      <div class="featured-header">
        <div class="featured-header--left">
          <h2 class="heading--4 fw-light text-color-quinary text-uppercase mb-0">
            FEATURED CARS
          </h2>
          <div class="featured-counter paragraph--small text-color-senary" v-if="displayCars.length > 0">
            {{ currentPage }} / {{ totalPages }}
          </div>
        </div>
        <div class="featured-header--right">
          <div class="d-flex align-items-center gap-3">
            <div class="dropdown" v-if="showFilter">
              <button
                class="btn btn--normal btn--secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedBrand === 'all' ? 'All Brands' : selectedBrand }}
              </button>
              <ul class="dropdown-menu">
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="selectedBrand = 'all'">All Brands</a>
                </li>
                <li v-for="brand in carBrands" :key="brand">
                  <a class="dropdown-item" href="#" @click.prevent="selectedBrand = brand">{{ brand }}</a>
                </li>
              </ul>
            </div>
            <div class="featured-nav">
              <button class="featured-nav-btn" :disabled="!canScrollLeft" @click="scrollCarousel('left')">
                <Icon icon="mdi:chevron-left" class="iconify" />
              </button>
              <button class="featured-nav-btn" :disabled="!canScrollRight" @click="scrollCarousel('right')">
                <Icon icon="mdi:chevron-right" class="iconify" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="container">
      <div class="text-center py-5">
        <div class="spinner-border text-light" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>

    <div v-else class="featured-carousel-wrap">
      <div class="featured-carousel" ref="carousel" @scroll="onCarouselScroll" @mouseenter="autoScrollPaused = true" @mouseleave="autoScrollPaused = false">
        <router-link
          v-for="car in displayCars"
          :key="car.id"
          :to="`/car/${slugify(car.brand)}/${slugify(car.model)}/${car.year || 'new'}/${car.id}`"
          class="featured-card"
        >
          <div class="featured-card--image">
            <img :src="car.image || '/frontend/assets/images/placeholder.jpg'" :alt="car.brand + ' ' + car.model" />
            <div class="featured-card--badge" :class="{
              'badge--available': car.status === 'Available',
              'badge--sold': car.status === 'Sold',
              'badge--sale': car.status === 'Sale',
            }">{{ car.status }}</div>
          </div>
          <div class="featured-card--body">
            <div class="featured-card--brand notranslate" translate="no">{{ car.brand }}</div>
            <div class="featured-card--model notranslate" translate="no">{{ car.model }}</div>
            <div class="featured-card--specs" v-if="car.year || car.mileage">
              <span>{{ car.year }}</span>
              <span v-if="car.mileage"> · {{ Number(car.mileage).toLocaleString('sk-SK') }} km</span>
            </div>
            <div class="featured-card--price notranslate" translate="no" v-if="car.status !== 'Sold'">
              <div class="price-netto" v-if="car.nettoPrice > 0">
                {{ formatPrice(car.nettoPrice) }} <span class="price-currency">€</span>
                <span class="price-label">NETTO</span>
              </div>
              <div class="price-gross" v-if="car.grossPrice > 0">
                {{ formatPrice(car.grossPrice) }} € VAT incl.
              </div>
            </div>
            <div class="featured-card--cta">
              <span>VIEW DETAILS</span>
              <Icon icon="mdi:arrow-right" class="iconify" />
            </div>
          </div>
        </router-link>
      </div>
    </div>

    <div class="container">
      <div class="featured-footer">
        <router-link
          to="/inventory"
          class="btn btn--normal btn--secondary px-5 justify-content-center"
        >VIEW FULL INVENTORY</router-link>
        <a href="https://t.me/selectedslovakia" target="_blank" class="btn btn--normal btn--primary">
          <Icon icon="uil:telegram" class="iconify" /> OFFERS ON TELEGRAM
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import axios from 'axios';
import { slugify } from '../seo.js';

export default {
  components: { Icon },
  props: {
    showFilter: {
      type: Boolean,
      default: true
    },
    excludeId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      cars: [],
      carBrands: [],
      selectedBrand: 'all',
      isLoading: false,
      canScrollLeft: false,
      canScrollRight: true,
      currentPage: 1,
      totalPages: 1,
      autoScrollInterval: null,
      autoScrollPaused: false
    };
  },
  computed: {
    displayCars() {
      let filtered = this.cars;

      // Exclude current car if on detail page
      if (this.excludeId) {
        filtered = filtered.filter(c => c.id != this.excludeId);
      }

      // Filter by brand
      if (this.selectedBrand !== 'all') {
        filtered = filtered.filter(c => c.brand === this.selectedBrand);
      }

      return filtered.slice(0, 12);
    }
  },
  watch: {
    displayCars() {
      this.$nextTick(() => this.updateScrollState());
    }
  },
  mounted() {
    this.fetchVehicles();
    this.startAutoScroll();
  },
  beforeUnmount() {
    this.stopAutoScroll();
  },
  methods: {
    slugify,
    async fetchVehicles() {
      this.isLoading = true;
      try {
        const response = await axios.get('/api/vehicles', {
          params: { limit: 12, sort: '-created_at', status: 'active' }
        });

        const vehiclesData = Array.isArray(response.data) ? response.data :
          (response.data.data ? response.data.data : [response.data]);

        const featuredVehicles = vehiclesData.filter(v => v.is_featured);
        const displayVehicles = featuredVehicles.length > 0 ? featuredVehicles : vehiclesData;

        this.cars = displayVehicles.map(vehicle => ({
          id: vehicle.id,
          brand: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          nettoPrice: vehicle.netto_price,
          grossPrice: vehicle.gross_price,
          mileage: vehicle.mileage,
          status: this.mapStatus(vehicle.status),
          image: vehicle.primary_image ? vehicle.primary_image.url : '/frontend/assets/images/placeholder.jpg'
        }));

        this.carBrands = [...new Set(displayVehicles.map(v => v.make))].filter(Boolean);
      } catch (error) {
        console.error('Error fetching featured vehicles:', error);
      } finally {
        this.isLoading = false;
      }
    },
    mapStatus(status) {
      const map = { 'active': 'Available', 'maintenance': 'Sale', 'inactive': 'Sold' };
      return map[status] || status;
    },
    formatPrice(price) {
      if (!price || price === 0) return 'POA';
      const num = typeof price === 'string' ? parseFloat(price) : price;
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true }).format(num);
    },
    startAutoScroll() {
      this.autoScrollInterval = setInterval(() => {
        if (this.autoScrollPaused) return;
        const carousel = this.$refs.carousel;
        if (!carousel) return;
        const card = carousel.querySelector('.featured-card');
        if (!card) return;
        const scrollAmount = card.offsetWidth + 16;
        if (carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth - 20) {
          carousel.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      }, 4000);
    },
    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    },
    scrollCarousel(direction) {
      this.autoScrollPaused = true;
      setTimeout(() => { this.autoScrollPaused = false; }, 10000);
      const carousel = this.$refs.carousel;
      if (!carousel) return;
      const card = carousel.querySelector('.featured-card');
      if (!card) return;
      const scrollAmount = card.offsetWidth + 16;
      const scrollBy = direction === 'left' ? -scrollAmount * 3 : scrollAmount * 3;
      carousel.scrollBy({ left: scrollBy, behavior: 'smooth' });
    },
    onCarouselScroll() {
      this.updateScrollState();
    },
    updateScrollState() {
      const carousel = this.$refs.carousel;
      if (!carousel) return;
      this.canScrollLeft = carousel.scrollLeft > 10;
      this.canScrollRight = carousel.scrollLeft < carousel.scrollWidth - carousel.clientWidth - 10;
      const card = carousel.querySelector('.featured-card');
      if (card) {
        const cardWidth = card.offsetWidth + 16;
        const cardsPerView = Math.round(carousel.clientWidth / cardWidth);
        const currentIndex = Math.round(carousel.scrollLeft / cardWidth);
        this.currentPage = Math.floor(currentIndex / cardsPerView) + 1;
        this.totalPages = Math.ceil(this.displayCars.length / cardsPerView);
      }
    }
  }
};
</script>

<style scoped>
/* ── Featured Cars Section ── */
.featured-section {
  overflow: hidden;
}

.featured-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding-bottom: 1.5rem;
  margin-bottom: 2.5rem;
}

.featured-header--left {
  display: flex;
  align-items: baseline;
  gap: 1.5rem;
}

.featured-counter {
  font-size: 0.75rem;
  letter-spacing: 0.15em;
  opacity: 0.4;
}

.featured-nav {
  display: flex;
  gap: 0.5rem;
}

.featured-nav-btn {
  width: 48px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
}

.featured-nav-btn .iconify {
  font-size: 1.4rem;
}

.featured-nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.featured-nav-btn:disabled {
  opacity: 0.2;
  cursor: default;
}

/* ── Carousel ── */
.featured-carousel-wrap {
  position: relative;
}

.featured-carousel {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 0 calc((100vw - 1320px) / 2);
}

.featured-carousel::-webkit-scrollbar {
  display: none;
}

/* ── Card ── */
.featured-card {
  flex: 0 0 calc((100% - 32px) / 3);
  min-width: 340px;
  scroll-snap-align: start;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  background: #35353a;
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
  transition: border-color 0.35s ease, transform 0.35s ease;
}

.featured-card:hover {
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-4px);
}

/* ── Card Image ── */
.featured-card--image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
}

.featured-card--image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
}

.featured-card:hover .featured-card--image img {
  transform: scale(1.05);
}

.featured-card--badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.badge--available { background: #fff; color: #000; }
.badge--sold { background: #2a2a2a; color: rgba(255,255,255,0.5); }
.badge--sale { background: #c9a84c; color: #000; }

/* ── Card Body ── */
.featured-card--body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.featured-card--brand {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.3);
}

.featured-card--model {
  font-size: 1.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #fff;
  line-height: 1.15;
}

.featured-card--specs {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.02em;
}

.featured-card--price {
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.price-netto {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.price-currency {
  font-size: 1.1rem;
  font-weight: 400;
}

.price-label {
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.3);
  margin-left: 0.15rem;
}

.price-gross {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.3);
}

.featured-card--cta {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.25s;
}

.featured-card:hover .featured-card--cta {
  color: #fff;
}

.featured-card--cta .iconify {
  font-size: 1rem;
  transition: transform 0.25s;
}

.featured-card:hover .featured-card--cta .iconify {
  transform: translateX(4px);
}

/* ── Footer ── */
.featured-footer {
  margin-top: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

/* ── Responsive ── */
@media (max-width: 1399px) {
  .featured-carousel {
    padding: 0 calc((100vw - 1140px) / 2);
  }
}

@media (max-width: 1199px) {
  .featured-carousel {
    padding: 0 calc((100vw - 960px) / 2);
  }
}

@media (max-width: 991px) {
  .featured-carousel {
    padding: 0 calc((100vw - 720px) / 2);
  }
  .featured-card {
    flex: 0 0 calc((100% - 16px) / 2);
    min-width: 300px;
  }
}

@media (max-width: 767px) {
  .featured-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  .featured-carousel {
    padding: 0 1rem;
    gap: 12px;
  }
  .featured-card {
    flex: 0 0 85vw;
    min-width: 280px;
  }
  .featured-card--body {
    padding: 1.25rem 1.25rem;
  }
  .featured-card--model {
    font-size: 1.3rem;
  }
  .featured-footer {
    flex-direction: column;
    margin-top: 2rem;
  }
  .featured-footer .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
